import * as React from "react";
import { cn } from "@/lib/cn";

interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "type"> {
  label?: React.ReactNode;
  description?: React.ReactNode;
}

const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  ({ className, label, description, id, ...props }, ref) => {
    const reactId = React.useId();
    const inputId = id ?? reactId;
    return (
      <label
        htmlFor={inputId}
        className={cn(
          "group flex cursor-pointer items-start gap-3 rounded-xl border border-border/60 bg-card/40 p-4 transition-all hover:border-primary/50 hover:bg-card/70",
          className,
        )}
      >
        <input
          ref={ref}
          id={inputId}
          type="checkbox"
          className="peer mt-1 size-5 shrink-0 cursor-pointer appearance-none rounded-md border border-border bg-background ring-offset-background transition-colors checked:border-primary checked:bg-gradient-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          {...props}
        />
        <div className="flex flex-col gap-1">
          {label ? (
            <span className="text-sm font-medium leading-tight text-foreground">{label}</span>
          ) : null}
          {description ? (
            <span className="text-xs leading-relaxed text-muted-foreground">{description}</span>
          ) : null}
        </div>
        <svg
          className="pointer-events-none absolute size-3 translate-x-1 translate-y-2 opacity-0 transition-opacity peer-checked:opacity-100"
          viewBox="0 0 12 12"
          fill="none"
        >
          <path
            d="M2 6.4L4.8 9.2L10 3.6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-primary-foreground"
          />
        </svg>
      </label>
    );
  },
);
Checkbox.displayName = "Checkbox";

export { Checkbox };
